/**
 * Wrap Auth0's `getAccessTokenSilently` so the result is guaranteed to be a
 * real string, typed as such.
 *
 * TypeScript's inferred type for a bare, zero-argument call to
 * `getAccessTokenSilently()` has been observed to resolve differently across
 * otherwise-identical installs — `Promise<string>` in some, `Promise<string
 * | undefined>` in others — which breaks any component whose prop declares
 * the stricter `() => Promise<string>` (a TS2322 at the JSX call site, not a
 * runtime bug). This wrapper's own signature is explicit either way, so it
 * satisfies those props regardless of how the environment infers Auth0's.
 *
 * Every caller here already treats a missing token as fatal — there is
 * nothing useful to do with `Authorization: Bearer undefined` — so throwing
 * is the correct behavior, not just a type-checker workaround.
 */
export function requireAccessToken(
  getAccessTokenSilently: () => Promise<string | undefined>
): () => Promise<string> {
  return async () => {
    const token = await getAccessTokenSilently();
    if (!token) throw new Error('Could not get an access token — please log in again.');
    return token;
  };
}
